import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMobileGridView } from '../hooks/useMobileGridView';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Card } from '../components/ui/Card';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { CustomerForm } from '../components/forms/CustomerForm';
import { SearchInput } from '../components/ui/SearchInput';
import { Pagination } from '../components/ui/Pagination';
import { usePagination } from '../hooks/usePagination';
import { isAdmin, isIntegrator, isSuperAdmin } from '../utils/roles';
import { apiList } from '../api/apiClient';
import { formatDate } from '../utils/formatters';
import { BulkActions, useSelection } from '../components/ui/BulkActions';
import { deleteCustomer } from '../api/customers';
import { refreshAllCounters } from '../utils/refreshCounters';
import { isAdminHost } from '../utils/host';
import { CountBand } from '../components/ui/CountBand';
import { ListFilterSelect } from '../components/ui/ListFilterSelect';
import { EntityMapView } from '../components/ui/EntityMapView';
import { BrandLoader } from '../components/ui/BrandLoader';

type ViewMode = 'list' | 'grid' | 'map';

export const Customers: React.FC = () => {
  const { user } = useAuth();
  const role = user?.role || 'customer';
  const [showModal, setShowModal] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [quickViewCustomer, setQuickViewCustomer] = useState<any>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('view:customers') as ViewMode) || 'list');
  useMobileGridView(setViewMode, 'grid');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const { selected, toggle, toggleAll, clear: clearSelection } = useSelection();

  const searchFields = useCallback((c: any) => [c.company, c.name, c.email, c.billing_email, c.integrator_name], []);
  const customerFilter = useCallback((c: any) => statusFilter === 'all' || String(c.status || 'active').toLowerCase() === statusFilter, [statusFilter]);
  const { search, setSearch, page, setPage, totalPages, filteredData, paginatedData, totalFiltered, startIndex, endIndex } = usePagination(customers, searchFields, customerFilter);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [custData, dealerData] = await Promise.all([
        apiList('customers'),
        apiList('dealers'),
      ]);
      const dealerMap = new Map((dealerData || []).map((d: any) => [d.uuid || d.id, d.name || d.company_name]));

      let filtered = custData || [];
      if (isIntegrator(role) && user?.integrator_id) {
        filtered = filtered.filter((c: any) => c.integrator_id === user.integrator_id || c.dealer_id === user.integrator_id);
      }

      setCustomers(filtered.map((c: any) => ({
        ...c,
        company: c.company || c.company_name || c.name,
        integrator_name: dealerMap.get(c.dealer_id) || c.integrator_name || '',
      })));
    } catch (e) {
      console.error('Failed to load customers:', e);
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = async () => {
    refreshAllCounters();
    setShowModal(false);
    setLoading(true);
    await load();
  };

  const columns = [
    { key: '_select', header: <input type="checkbox" checked={selected.size === customers.length && customers.length > 0} onChange={() => toggleAll(customers.map(d => d.id))} className="w-4 h-4 rounded border-gray-300" />, render: (c: any) => <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} onClick={(e: any) => e.stopPropagation()} className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />, className: 'w-8' },
    { key: 'company', header: 'Company', sticky: 'primary' as const, render: (c: any) => <Link onClick={(e) => e.stopPropagation()} to={`/customers/${c.uuid || c.id}`} className="font-medium text-brand-700 hover:underline">{c.company}</Link> },
    { key: 'integrator_name',          header: 'Dealer',       render: (c: any) => c.integrator_name ? <span className="text-gray-700">{c.integrator_name}</span> : <span className="text-gray-400">—</span> },
    { key: 'total_properties',         header: 'Properties',   render: (c: any) => c.total_properties         || 0 },
    { key: 'total_units',              header: 'Units',        render: (c: any) => c.total_units              || 0 },
    { key: 'total_intercoms',          header: 'Intercoms',    render: (c: any) => c.total_intercoms          || 0 },
    { key: 'total_devices',            header: 'Devices',
      render: (c: any) => (Number(c.total_devices) || 0) + (Number(c.total_acs_devices) || 0) },
    { key: 'total_tenants',            header: 'Tenants',      render: (c: any) => c.total_tenants            || 0 },
    { key: 'total_guests',             header: 'Guests',       render: (c: any) => c.total_guests             || 0 },
    { key: 'total_phones',             header: 'Phones',       render: (c: any) => c.total_phones             || 0 },
    { key: 'status', header: 'Status', render: (c: any) => <Badge status={c.status || 'active'}>{c.status || 'active'}</Badge> },
    { key: '_actions', header: '', render: (c: any) => (
      <div onClick={(e) => e.stopPropagation()}><button onClick={() => navigate(`/customers/${c.uuid || c.id}`)} className="row-open">Open <span aria-hidden="true">→</span></button></div>
    ), className: 'w-24', sticky: 'actions' as const },
  ];

  if (loading) return <BrandLoader label="Loading customers" />;

  return (
    <div className="shadow-list-page has-prototype-toolbar">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-gray-900">Customers</h1><p className="text-gray-500 mt-1">Manage customer accounts</p></div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => { setViewMode('list'); localStorage.setItem('view:customers', 'list'); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              title="List View"
            >List</button>
            <button
              onClick={() => { setViewMode('grid'); localStorage.setItem('view:customers', 'grid'); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              title="Grid View"
            >Grid</button>
          </div>
          <Button onClick={() => setShowModal(true)}><svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>Add Customer</Button>
        </div>
      </div>
      <CountBand matches={totalFiltered} total={customers.length} detail="Customers matching filters in the current accessible portfolio" />
      <div className="prototype-toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search customers..." />
        <label><small>Status</small><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}><option value="all">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        <div className="prototype-view-toggle"><button className={viewMode === 'list' ? 'active' : ''} onClick={() => { setViewMode('list'); localStorage.setItem('view:customers','list'); }}>Table</button><button className={viewMode === 'grid' ? 'active' : ''} onClick={() => { setViewMode('grid'); localStorage.setItem('view:customers','grid'); }}>Grid</button><button className={viewMode === 'map' ? 'active' : ''} onClick={() => { setViewMode('map'); localStorage.setItem('view:customers','map'); }}>Map</button></div>
        <Button onClick={() => setShowModal(true)}>Add Customer</Button>
      </div>
      {/* Mobile: card grid (no list/table on small screens). */}
      <div className="md:hidden grid grid-cols-1 gap-4 mb-4">
        {paginatedData.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">No customers found</div>
        ) : (
          paginatedData.map((c: any) => {
            const contactName = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || c.contact_name;
            const addressLine = [c.address || c.address1, c.address2, c.city, c.state, c.zip || c.zip_code || c.postal_code].filter(Boolean).join(', ');
            const contactEmail = c.contact_email || c.email;
            const contactPhone = c.contact_phone || c.phone;
            return (
            <div key={c.id} onClick={() => setQuickViewCustomer(c)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{c.company_name || c.name}</h3>
                  {c.integrator_name && <p className="text-xs text-gray-500 truncate">Dealer: {c.integrator_name}</p>}
                </div>
                <Badge status={c.status || 'active'}>{c.status || 'active'}</Badge>
              </div>
              <div className="customer-card-address"><span>Address</span><p>{addressLine || 'No address on file'}</p></div>
              <div className="customer-card-contact">
                <span>Primary contact</span><strong>{contactName || 'No contact on file'}</strong>
                {contactEmail && <a href={`mailto:${contactEmail}`} onClick={(e) => e.stopPropagation()}>{contactEmail}</a>}
                {contactPhone && <a href={`tel:${contactPhone}`} onClick={(e) => e.stopPropagation()}>{contactPhone}</a>}
              </div>
              <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}>
                <summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary>
                <div><article><span>Properties</span><strong>{c.total_properties || 0}</strong></article><article><span>Units</span><strong>{c.total_units || 0}</strong></article><article><span>Tenants</span><strong>{c.total_tenants || 0}</strong></article><article><span>Devices</span><strong>{(Number(c.total_devices) || 0) + (Number(c.total_acs_devices) || 0)}</strong></article><article><span>Intercoms</span><strong>{c.total_intercoms || 0}</strong></article><article><span>Mobile</span><strong>{c.total_active_activations ?? c.total_activations ?? 0}</strong></article></div>
              </details>
              <div className="mobile-card-actions">
                <button onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.uuid || c.id}`); }} className="row-open">Open <span aria-hidden="true">→</span></button>
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Desktop: full searchable table or grid (hidden on small screens). */}
      {viewMode === 'list' ? (
        <Card padding={false} className="hidden md:block">
          <Table
            columns={columns}
            data={paginatedData}
            onRowClick={(c: any) => navigate(`/customers/${c.uuid || c.id}`)}
            emptyMessage="No customers found"
          />
          <Pagination page={page} totalPages={totalPages} totalItems={totalFiltered} startIndex={startIndex} endIndex={endIndex} onPageChange={setPage} />
        </Card>
      ) : viewMode === 'map' ? (
        <EntityMapView addressLabel="company office address" onOpen={navigate} items={filteredData.map((c:any) => ({ id:String(c.uuid || c.id), name:c.company || c.company_name || c.name || 'Customer', address:[c.address || c.address1,c.city,c.state,c.zip || c.zip_code].filter(Boolean).join(', ') || c.billing_address || '', contact:[c.contact_first_name,c.contact_last_name].filter(Boolean).join(' ') || c.contact_name, email:c.contact_email || c.email, phone:c.contact_phone || c.phone, status:c.status, lat:Number(c.latitude ?? c.lat ?? c.address_lat), lng:Number(c.longitude ?? c.lng ?? c.lon ?? c.address_lng), detailPath:`/customers/${c.uuid || c.id}`, metrics:[{label:'Properties',value:c.total_properties||0},{label:'Units',value:c.total_units||0},{label:'Tenants',value:c.total_tenants||0},{label:'Devices',value:(Number(c.total_devices)||0)+(Number(c.total_acs_devices)||0)},{label:'Intercoms',value:c.total_intercoms||0},{label:'Mobile',value:c.total_active_activations??c.total_activations??0}] }))} />
      ) : (
        <div className="hidden md:block">
          {paginatedData.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-sm text-gray-400">No customers found</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {paginatedData.map((c: any) => {
                const contactName = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ') || c.contact_name;
                const addressLine = [c.address || c.address1, c.address2, c.city, c.state, c.zip || c.zip_code || c.postal_code].filter(Boolean).join(', ');
                const contactEmail = c.contact_email || c.email;
                const contactPhone = c.contact_phone || c.phone;
                return (
                <div key={c.id} onClick={() => navigate(`/customers/${c.uuid || c.id}`)} className="customer-grid-card bg-white border border-gray-200 cursor-pointer hover:border-brand-400 transition-colors">
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{c.company || c.company_name || c.name}</h3>
                      {c.integrator_name && <p className="text-xs text-gray-500 truncate">Dealer: {c.integrator_name}</p>}
                    </div>
                    <Badge status={c.status || 'active'}>{c.status || 'active'}</Badge>
                  </div>
                  <div className="customer-card-address"><span>Address</span><p>{addressLine || 'No address on file'}</p></div>
                  <div className="customer-card-contact">
                    <span>Primary contact</span><strong>{contactName || 'No contact on file'}</strong>
                    {contactEmail && <a href={`mailto:${contactEmail}`} onClick={(e) => e.stopPropagation()}>{contactEmail}</a>}
                    {contactPhone && <a href={`tel:${contactPhone}`} onClick={(e) => e.stopPropagation()}>{contactPhone}</a>}
                  </div>
                  <details className="customer-card-metrics" onClick={(e) => e.stopPropagation()}>
                    <summary><span className="show-label">View metrics</span><span className="hide-label">Hide metrics</span><b aria-hidden="true">⌄</b></summary>
                    <div>
                      <article><span>Properties</span><strong>{c.total_properties || 0}</strong></article><article><span>Units</span><strong>{c.total_units || 0}</strong></article><article><span>Tenants</span><strong>{c.total_tenants || 0}</strong></article><article><span>Devices</span><strong>{(Number(c.total_devices) || 0) + (Number(c.total_acs_devices) || 0)}</strong></article><article><span>Intercoms</span><strong>{c.total_intercoms || 0}</strong></article><article><span>Mobile</span><strong>{c.total_active_activations ?? c.total_activations ?? 0}</strong></article>
                    </div>
                  </details>
                </div>
                );
              })}
            </div>
          )}
          <div className="mt-4">
            <Pagination page={page} totalPages={totalPages} totalItems={totalFiltered} startIndex={startIndex} endIndex={endIndex} onPageChange={setPage} />
          </div>
        </div>
      )}
      <Modal open={!!quickViewCustomer} onClose={() => setQuickViewCustomer(null)} title={`${quickViewCustomer?.name || quickViewCustomer?.company_name || 'Customer'} — Quick View`} size="lg">
        {quickViewCustomer && (() => {
          const c = quickViewCustomer;
          const contactName = [c.contact_first_name, c.contact_last_name].filter(Boolean).join(' ');
          const addressLine = [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ');
          const num = (n: any) => Number(n || 0).toLocaleString();
          const MetricRow = ({ label, month, lifetime, muted }: { label: string; month?: any; lifetime?: any; muted?: boolean }) => (
            <div className={`grid grid-cols-[1fr_auto_auto] items-baseline gap-3 py-1.5 ${muted ? 'text-gray-400' : ''}`}>
              <span className="text-sm">{label}</span>
              <span className={`text-sm font-semibold tabular-nums w-12 text-right ${muted ? '' : 'text-gray-900'}`}>{num(month)}</span>
              <span className="text-xs text-gray-500 tabular-nums w-16 text-right">{lifetime !== undefined ? num(lifetime) : ''}</span>
            </div>
          );
          const ColHeader = ({ title }: { title: string }) => (
            <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-3 mb-1 pb-1 border-b border-gray-200">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{title}</p>
              <p className="text-[10px] font-semibold uppercase text-gray-400 w-12 text-right">MTD</p>
              <p className="text-[10px] font-semibold uppercase text-gray-400 w-16 text-right">Lifetime</p>
            </div>
          );
          return (
            <div className="space-y-5">
              {/* ─── Hero Cards ─── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Properties</p>
                    <p className="text-3xl font-extrabold text-gray-900">{num(c.total_properties)}</p>
                  </div>
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Units</p>
                    <p className="text-3xl font-extrabold text-gray-900">{num(c.total_units)}</p>
                  </div>
                </div>
              </div>

              {/* ─── Three column metrics ─── */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:divide-x md:divide-gray-200">
                <div className="md:pr-6">
                  <ColHeader title="Users & Activity" />
                  <MetricRow label="Tenants" month={c.total_tenants} />
                  <MetricRow label="Guests" month={c.total_guests} />
                  <MetricRow label="Mobile Apps" month={c.total_activations} muted={!c.total_activations} />
                  <MetricRow label="ACS Systems" month={c.total_systems} muted={!c.total_systems} />
                  <MetricRow label="Devices" month={c.total_devices} />
                  <MetricRow label="Phone Numbers" month={c.total_phones} />
                </div>
                <div className="md:px-6">
                  <ColHeader title="Usage Metrics" />
                  <MetricRow label="Unlocks" month={c.door_unlocks_this_month} lifetime={c.door_unlocks_lifetime} />
                  <MetricRow label="Page Views" month={c.visitor_page_views_this_month} lifetime={c.visitor_page_views_lifetime} />
                  <MetricRow label="Intercom Calls" month={c.intercom_calls_this_month} lifetime={c.intercom_calls_lifetime} />
                  <MetricRow label="Face Scans" month={c.face_id_verifications_this_month} lifetime={c.face_id_verifications_lifetime} />
                  <MetricRow label="BLE Reads" month={c.ble_credential_reads_this_month} lifetime={c.ble_credential_reads_lifetime} />
                  <MetricRow label="Cellular MB" month={c.cellular_data_mb_this_month} lifetime={c.cellular_data_mb_lifetime} />
                </div>
                <div className="md:pl-6">
                  <ColHeader title="Communication" />
                  <MetricRow label="Video" month={c.video_minutes_this_month} lifetime={c.video_minutes_lifetime} />
                  <MetricRow label="Voice" month={c.voice_minutes_this_month} lifetime={c.voice_minutes_lifetime} />
                  <MetricRow label="SMS" month={c.sms_sent_this_month} lifetime={c.sms_sent_lifetime} />
                  <MetricRow label="Pushes" month={c.push_notifications_sent_this_month} lifetime={c.push_notifications_sent_lifetime} />
                </div>
              </div>

              {/* ─── Contact Strip ─── */}
              <div className="border-t border-gray-200 pt-4 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Contact</p>
                  <p className="font-medium text-gray-900">{contactName || c.name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium text-gray-900 truncate">{c.contact_email || c.email || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{c.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Joined</p>
                  <p className="font-medium text-gray-900">{c.created_at ? formatDate(c.created_at) : '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="font-medium text-gray-900">{addressLine || '—'}</p>
                </div>
                {isAdminHost() && (
                  <div>
                    <p className="text-xs text-gray-500">Stripe</p>
                    <p className="font-mono text-xs text-gray-900 truncate">{c.stripe_customer_id || <span className="text-yellow-600">—</span>}</p>
                  </div>
                )}
                {isAdminHost() && (
                  <div>
                    <p className="text-xs text-gray-500">Open Balance</p>
                    <p className={`font-medium ${(c.open_balance || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>${num(c.open_balance)}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-3 border-t border-gray-200">
                <button onClick={() => navigate(`/customers/${c.uuid || c.id}`)} className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">View Full Details</button>
                <button onClick={() => navigate(`/customers/${c.uuid || c.id}?edit=true`)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50">Edit</button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Customer">
        <CustomerForm onSubmit={handleCreated} onCancel={() => setShowModal(false)} />
      </Modal>
      <BulkActions selectedIds={selected} onClearSelection={clearSelection} onDelete={async (ids) => { await Promise.all(ids.map(id => deleteCustomer(id))); setLoading(true); await load(); }} entityName="customers" />
    </div>
  );
};
